import React from 'react';
import useWorkClock, { formatElapsed } from '../../hook/useWorkClock';
import WorkClockControls from '../WorkClockControls/WorkClockControls';
import WorkClockShiftSetup from '../WorkClockShiftSetup/WorkClockShiftSetup';
import {
  TIME_LOG_ACTION,
  TIME_LOG_ACTION_LABEL,
  TIME_LOG_STATE,
} from '../../constants/timeLog';
import { convert } from '../../utils/moment';
import { formatDeviceLocation, formatUserLocation } from '../../utils/timeLogDisplay';
import './WorkClock.scss';

const formatDuration = (minutes) => {
  if (minutes == null) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const actionBadgeClass = (action) => {
  if (action === TIME_LOG_ACTION.OUT) return 'work-clock-action-badge--out';
  if (action === TIME_LOG_ACTION.PAUSE) return 'work-clock-action-badge--pause';
  if (action === TIME_LOG_ACTION.RESUME) return 'work-clock-action-badge--resume';
  return 'work-clock-action-badge--in';
};

const ActionBadge = ({ action }) => (
  <span className={`work-clock-action-badge ${actionBadgeClass(action)}`}>
    {TIME_LOG_ACTION_LABEL[action] || action}
  </span>
);

export default function WorkClock({ logLimit = 20, variant = 'embedded', showControls }) {
  const controlsVisible = showControls ?? variant !== 'page';
  const {
    clockState,
    clockStateLabel,
    elapsedSeconds,
    logs,
    actionLoading,
    handleClockIn,
    handlePause,
    handleResume,
    handleClockOut,
  } = useWorkClock({ logLimit });

  const isPage = variant === 'page';
  const isOnShift = clockState !== TIME_LOG_STATE.OFF;

  const badgeClass = {
    [TIME_LOG_STATE.OFF]: 'off-shift',
    [TIME_LOG_STATE.ACTIVE]: 'on-shift',
    [TIME_LOG_STATE.PAUSED]: 'paused-shift',
  }[clockState] || 'off-shift';

  return (
    <div className={`work-clock-card${isPage ? ' work-clock-card--page' : ''}`}>
      <div className="work-clock-header">
        <div>
          <h5>{isPage ? 'Time Tracking' : 'Work Clock'}</h5>
          <p className="work-clock-subtitle">
            {isPage
              ? 'Select your device below, then use the header controls to clock in, pause, or clock out.'
              : 'Clock In → Pause → Clock Out — available in the header on every page'}
          </p>
        </div>
        <span className={`work-clock-badge ${badgeClass}`}>
          {clockStateLabel}
        </span>
      </div>

      {isPage && (
        <div className="work-clock-elapsed-wrap">
          <p className="work-clock-elapsed-label">
            {isOnShift ? 'Current shift' : 'Not clocked in'}
          </p>
          <div className={`work-clock-elapsed${isOnShift ? '' : ' work-clock-elapsed--idle'}`}>
            {isOnShift ? formatElapsed(elapsedSeconds) : '00:00:00'}
          </div>
        </div>
      )}

      {!isPage && isOnShift && (
        <div className="work-clock-elapsed">{formatElapsed(elapsedSeconds)}</div>
      )}

      {isPage && <WorkClockShiftSetup />}

      {controlsVisible && (
        <WorkClockControls
          variant={variant === 'page' ? 'page' : 'default'}
          clockState={clockState}
          actionLoading={actionLoading}
          onClockIn={handleClockIn}
          onPause={handlePause}
          onResume={handleResume}
          onClockOut={handleClockOut}
        />
      )}

      <div className="work-clock-log-section">
        <div className="work-clock-log-title">
          <span>{isPage ? 'Work log history' : 'Recent work logs'}</span>
          {logs.length > 0 && (
            <span className="work-clock-log-count">
              {logs.length} {logs.length === 1 ? 'entry' : 'entries'}
            </span>
          )}
        </div>
        <div className="work-clock-log-table-wrap">
          <table className="table work-clock-table">
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>Action</th>
                <th>Device</th>
                <th>Name</th>
                <th>Your location</th>
                <th>Device location</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="work-clock-empty">
                    {isPage
                      ? 'No work clock logs yet. Select a device and clock in from the header to start tracking.'
                      : 'No work clock logs yet'}
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log._id}>
                    <td>{convert(log.timestamp, 'DD MMM YYYY HH:mm:ss')}</td>
                    <td>
                      <ActionBadge action={log.action} />
                    </td>
                    <td>{log.deviceName || '—'}</td>
                    <td>{log.notes || '—'}</td>
                    <td>{formatUserLocation(log.geofenceData)}</td>
                    <td>{formatDeviceLocation(log.deviceSnapshot)}</td>
                    <td>{log.action === TIME_LOG_ACTION.OUT ? formatDuration(log.durationMinutes) : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
